import { state } from '../state';
import type { Item } from '../types';
import { dom } from '../dom';
import { COLORS, type AppDataEntry } from '../appData';
type AppEntry = AppDataEntry;
import {
  CX, CY, BASE_SIZE,
  RINGS, SLOT_COUNT_PER_RING,
  EXTRA_RING_RADIUS_STEP, EXTRA_RING_SCALE_CAP,
  EXTRA_RING_OFFSET_A, EXTRA_RING_OFFSET_B,
  ORGANISM_SCALE_MIN, ORGANISM_SCALE_MAX,
  INSTALLER_STATES,
  FLUID_TARGET_LERP_BASE, FLUID_TARGET_LERP_BOOST, ORGANISM_OUTPUT_LERP_MULT,
  ORGANISM_PAIR_GAP, ORGANISM_TUBE_INNER_EDGE_PAD, ORGANISM_TUBE_OUTER_EDGE_PAD,
  ORGANISM_BOUNDARY_PADDING, ORGANISM_PAIR_PASSES, ORGANISM_FINAL_PASSES,
  FLUID_SCALE_LERP_HIGHLIGHT, ORGANISM_FINAL_SHRINK, ORGANISM_FINAL_MAX_SHRINK,
  ORGANISM_ANCHOR_PULL, ORGANISM_PRIMARY_SOLVER_PRIORITY,
  USE_SOLID_ICON_COLOR, SOLID_ICON_COLOR,
} from '../config';
import { clamp } from '../utils/math';
import { sampleAverageColor } from '../utils/imageSampler';
import { isReducedMotion } from '../utils/reducedMotion';
import type { LayoutState, LayoutFrameCtx, LayoutConfigInput } from './tubeLayoutEngine';

// Lines point to center. Used by primeSettledScales and getHomeLayoutConfig.
export const LINE_CENTER_RADIUS = 100;

/** Shape of each ring descriptor in the RINGS array. */
interface RingDescriptor {
  count: number;
  radius: number;
  startAngle: number;
  endAngle: number;
  angleOffset: number;
  rotOffset: number;
  scale: number;
}

// selectItem is still in the inline script (will be extracted in Task 21).
// Populated by setSelectItemRef() called from index.html at init() time.
let selectItem: (item: Item) => void = (_item) => {};
export function setSelectItemRef(fn: (item: Item) => void): void { selectItem = fn; }

function calcAngle(ring: RingDescriptor, index: number): number {
  const range = ring.endAngle - ring.startAngle;
  const isFullCircle = Math.abs(Math.abs(range) - 360) < 0.01;
  let angle;
  if (ring.count > 1) {
    const step = isFullCircle ? range / ring.count : range / (ring.count - 1);
    angle = ring.startAngle + step * index;
  } else {
    angle = ring.startAngle + range * 0.5;
  }
  return angle + ring.angleOffset + ring.rotOffset;
}

function posFromAngle(radius: number, deg: number): { x: number; y: number } {
  const rad = deg * Math.PI / 180;
  return {
    x: Math.cos(rad) * radius + CX,
    y: -Math.sin(rad) * radius + CY,
  };
}

export function isInstallerFlowActive(): boolean {
  return state.installerFlow != null && state.installerFlow.state !== INSTALLER_STATES.IDLE;
}

export function isScannerInteractiveState(): boolean {
  if (!state.installerFlow) return false;
  return state.installerFlow.state === INSTALLER_STATES.SCANNER_OPENING
    || state.installerFlow.state === INSTALLER_STATES.SCANNING
    || state.installerFlow.state === INSTALLER_STATES.PROCESSING;
}

export function isScannerCameraLockActive(): boolean {
  if (!state.installerItem || state.appState !== 'home') return false;
  if (!state.installerFlow) return false;
  return state.installerFlow.state === INSTALLER_STATES.SCANNER_OPENING
    || state.installerFlow.state === INSTALLER_STATES.SCANNING
    || state.installerFlow.state === INSTALLER_STATES.PROCESSING
    || state.installerFlow.state === INSTALLER_STATES.SCANNER_CLOSING;
}

export function setInstallerFlowState(nextState: string): void {
  if (!state.installerFlow) return;
  if (state.installerFlow.state === nextState) return;
  console.log(`[Loop OS] Installer: ${state.installerFlow.state} -> ${nextState}`);
  state.installerFlow.state = nextState;
}

function getExtendedRingAngleOffset(ringIndex: number): number {
  if (ringIndex < RINGS.length) return RINGS[ringIndex]!.angleOffset;
  if (RINGS.length < 2) return RINGS[RINGS.length - 1]!.angleOffset;
  const seqIndex = ringIndex - (RINGS.length - 2);
  return seqIndex % 2 === 0 ? EXTRA_RING_OFFSET_A : EXTRA_RING_OFFSET_B;
}

function getRingLayout(ringIndex: number): RingDescriptor {
  if (ringIndex < RINGS.length) return RINGS[ringIndex]!;
  const outer = RINGS[RINGS.length - 1]!;
  const extra = ringIndex - (RINGS.length - 1);
  return {
    count: SLOT_COUNT_PER_RING,
    radius: outer.radius + EXTRA_RING_RADIUS_STEP * extra,
    startAngle: outer.startAngle,
    endAngle: outer.endAngle,
    angleOffset: getExtendedRingAngleOffset(ringIndex),
    rotOffset: outer.rotOffset,
    scale: EXTRA_RING_SCALE_CAP,
  };
}

export function getSlotLayout(slotIndex: number): { ringIndex: number; slotInRing: number; x: number; y: number; size: number } {
  // Fill inner ring first (to max SLOT_COUNT_PER_RING), then middle, then
  // outer, then extra rings beyond. This is the OS-wide guideline: rows
  // fill from the inside outward.
  const ringIndex = Math.floor(slotIndex / SLOT_COUNT_PER_RING);
  const slotInRing = slotIndex % SLOT_COUNT_PER_RING;
  const ring = getRingLayout(ringIndex);
  const angle = calcAngle(ring, slotInRing);
  const pos = posFromAngle(ring.radius, angle);
  const size = BASE_SIZE * ring.scale;
  return { ringIndex, slotInRing, x: pos.x, y: pos.y, size };
}

export function ensureRingGuide(ringIndex: number): HTMLElement {
  if (state.ringGuideEls[ringIndex]) return state.ringGuideEls[ringIndex];
  const ring = getRingLayout(ringIndex);
  const guide = document.createElement('div');
  guide.className = 'ring-guide';
  const d = ring.radius * 2;
  guide.style.width = d + 'px';
  guide.style.height = d + 'px';
  dom.menuContainer.appendChild(guide);
  state.ringGuideEls[ringIndex] = guide;
  if (state.appState === 'home' || state.appState === 'unlocking') guide.classList.add('visible');
  return guide;
}

export function applyItemAppearance(item: Item): void {
  // Always set background-color: this is the "placeholder circle" that
  // shows while the cover PNG is decoding (on a cold device paint) or if
  // the cover fails to load. CSS paints `background-image` on top of
  // `background-color`, so once the image is decoded it covers the
  // colour; until then the designer-chosen `edgeColor` is visible. This
  // also lets covers with transparent pixels anchor against the edgeColor
  // instead of a see-through hole.
  item.inner.style.backgroundColor = item.color;
  if (item.cover) {
    item.inner.style.backgroundImage = `url('${item.cover}')`;
    item.inner.style.backgroundSize = 'cover';
    item.inner.style.backgroundPosition = 'center';
    if (!item.sampledLineColor) {
      const probe = new Image();
      probe.crossOrigin = 'anonymous';
      probe.onload = () => {
        const sampled = sampleAverageColor(probe);
        if (sampled) item.sampledLineColor = sampled;
      };
      probe.src = item.cover;
    }
  } else {
    item.inner.style.backgroundImage = '';
  }
}

export function getItemBaseScale(item: Item): number {
  const boost = item && Number.isFinite(item.organismScaleBoost) ? item.organismScaleBoost : 0;
  return clamp(1 + boost, ORGANISM_SCALE_MIN, ORGANISM_SCALE_MAX);
}

export function getItemSettledScale(item: Item): number {
  const baseScale = getItemBaseScale(item);
  if (item.settledScale != null && Number.isFinite(item.settledScale) && item.settledScale > 0) {
    const settled = clamp(item.settledScale, ORGANISM_SCALE_MIN, ORGANISM_SCALE_MAX);
    const maxDrift = 0.04;
    return clamp(settled, baseScale - maxDrift, baseScale + maxDrift);
  }
  return baseScale;
}

export function updateSettledScaleCache(activeItems: Item[] = state.items, options: { fromCurrent?: boolean } = {}): void {
  if (!Array.isArray(activeItems)) return;
  const fromCurrent = !!options.fromCurrent;
  for (const item of activeItems) {
    if (!item) continue;
    const measured = fromCurrent && Number.isFinite(item.currentScale) && item.currentScale > 0
      ? item.currentScale
      : getItemBaseScale(item);
    item.settledScale = clamp(measured, ORGANISM_SCALE_MIN, ORGANISM_SCALE_MAX);
  }
}

export function primeSettledScales(): void {
  if (!Array.isArray(state.items) || state.items.length === 0) return;
  const preSolvePasses = 30;
  const pinnedItems = new Set<Item>();
  for (const item of state.items) {
    const baseScale = clamp(
      1 + (Number.isFinite(item.organismScaleBoost) ? item.organismScaleBoost : 0),
      ORGANISM_SCALE_MIN,
      ORGANISM_SCALE_MAX
    );
    item.currentX = item.baseX;
    item.currentY = item.baseY;
    item.flowX = item.baseX;
    item.flowY = item.baseY;
    item.targetX = item.baseX;
    item.targetY = item.baseY;
    item.currentScale = baseScale;
    item.organismDesiredScale = baseScale;
  }
  for (let pass = 0; pass < preSolvePasses; pass++) {
    stepTubeLayout({
      items: state.items,
      highlighted: null,
      cx: CX,
      cy: CY,
      centerGuardRadius: LINE_CENTER_RADIUS + 4,
      applyItemTransform,
      getRenderScale: getItemRenderScale,
      config: getHomeLayoutConfig(),
    }, {
      focusX: CX,
      focusY: CY,
      posLerp: 1,
      obstacles: [],
      pinnedItems,
    });
  }
  updateSettledScaleCache(state.items, { fromCurrent: true });
}

export function getItemRenderScale(item: Item): number {
  const scannerScale = item && item.scannerActive
    ? Math.max(1, item.scannerScale || 1)
    : 1;
  const uiScale = Number.isFinite(item.uiScaleMul) ? Math.max(0, item.uiScaleMul) : 1;
  // pressScaleMul is a pure render-time multiplier — it does NOT feed back
  // into the layout solver via targetScale, so press feedback is visible
  // even when the home interactive phase is gated (e.g. during launch).
  const pressScale = Number.isFinite(item.pressScaleMul) ? Math.max(0, item.pressScaleMul) : 1;
  return Math.max(item.currentScale || 0, 0.001) * scannerScale * uiScale * pressScale;
}

export function getOrganismItemRadius(item: Item, scale: number): number {
  const fallbackScale = Number.isFinite(item && item.currentScale) ? item.currentScale : 1;
  const resolvedScale = Math.max(Number.isFinite(scale) ? scale : fallbackScale, 0.001);
  return Math.max(1, item.size * 0.5 * resolvedScale);
}

export function applyItemTransform(item: Item): void {
  const ox = item.currentX - CX;
  const oy = item.currentY - CY;
  item.el.style.transform = `translate3d(${ox}px, ${oy}px, 0) scale(${getItemRenderScale(item)})`;
}

export function applyItemSlot(item: Item, slotIndex: number, snap = false): void {
  const slot = getSlotLayout(slotIndex);
  ensureRingGuide(slot.ringIndex);
  item.slotIndex = slotIndex;
  item.ringIndex = slot.ringIndex;
  item.baseX = slot.x;
  item.baseY = slot.y;
  item.size = slot.size;
  item.el.style.width = slot.size + 'px';
  item.el.style.height = slot.size + 'px';
  item.el.style.marginLeft = (-slot.size / 2) + 'px';
  item.el.style.marginTop = (-slot.size / 2) + 'px';
  if (!snap) return;
  item.currentX = slot.x;
  item.currentY = slot.y;
  item.flowX = slot.x;
  item.flowY = slot.y;
  item.targetX = slot.x;
  item.targetY = slot.y;
  applyItemTransform(item);
}

export function setItemPose(item: Item, x: number, y: number, scale = 1): void {
  item.currentX = x;
  item.currentY = y;
  item.flowX = x;
  item.flowY = y;
  item.targetX = x;
  item.targetY = y;
  item.currentScale = scale;
  applyItemTransform(item);
  item.visualOpacity = 1;
  item.el.style.opacity = '1';
  item.visualTint = 0;
  if (item.inner) item.inner.style.filter = '';
}

export function setItemInstallArcVisible(item: Item, visible: boolean): void {
  if (!item || !item.installArcEl) return;
  if (visible) {
    item.el.classList.add('installing');
    item.installArcEl.classList.add('active');
  } else {
    item.el.classList.remove('installing');
    item.installArcEl.classList.remove('active');
  }
}

let itemUidCounter = 1;

export function createItemFromData(appData: AppEntry, slotIndex: number): Item {
  const kind = appData.kind || (appData.appId ? 'activity' : 'placeholder');
  // Solid-color mode forces a uniform tone for every icon EXCEPT the
  // installer ("add activity"), whose explicit edgeColor wins so it can
  // stand out as the sole tinted icon on the home screen.
  const color: string = (USE_SOLID_ICON_COLOR && kind !== 'installer')
    ? SOLID_ICON_COLOR
    : (appData.edgeColor || COLORS[slotIndex % COLORS.length] || '#888888');

  const el = document.createElement('div');
  el.className = 'item';
  const inner = document.createElement('div');
  inner.className = 'item-inner';
  let scannerLayerEl: HTMLElement | null = null;
  let scannerVideoEl: HTMLVideoElement | null = null;
  let scannerFallbackEl: HTMLElement | null = null;
  if (kind === 'installer') {
    scannerLayerEl = document.createElement('div');
    scannerLayerEl.className = 'item-scanner-layer';
    scannerVideoEl = document.createElement('video');
    scannerVideoEl.className = 'item-scanner-video';
    scannerVideoEl.autoplay = true;
    scannerVideoEl.playsInline = true;
    scannerVideoEl.muted = true;
    scannerFallbackEl = document.createElement('div');
    scannerFallbackEl.className = 'item-scanner-fallback';
    scannerLayerEl.appendChild(scannerVideoEl);
    scannerLayerEl.appendChild(scannerFallbackEl);
    inner.appendChild(scannerLayerEl);
  }
  const installArcEl = document.createElement('div');
  installArcEl.className = 'item-install-arc';
  el.appendChild(inner);
  el.appendChild(installArcEl);
  dom.menuContainer.appendChild(el);

  const item = {
    uid: itemUidCounter++,
    el,
    inner,
    installArcEl,
    name: appData.name || 'app',
    color,
    sampledLineColor: null as string | null,
    cover: appData.cover || null,
    appId: appData.appId || null,
    kind,
    projectId: appData.projectId || null,
    sessionInstalled: !!appData.sessionInstalled,
    installState: appData.installState || 'ready',
    baseX: CX,
    baseY: CY,
    currentX: CX,
    currentY: CY,
    flowX: CX,
    flowY: CY,
    targetX: CX,
    targetY: CY,
    size: BASE_SIZE,
    currentScale: 0,
    settledScale: null as number | null,
    organismDesiredScale: 1,
    organismScaleBoost: Number.isFinite(appData.organismScaleBoost) ? (appData.organismScaleBoost as number) : 0,
    scannerLayerEl,
    scannerVideoEl,
    scannerFallbackEl,
    scannerActive: false,
    scannerOpenProgress: 0,
    scannerScale: 1,
    scannerVideoAlpha: 0,
    scannerVideoLive: false,
    visualOpacity: 1,
    visualTint: 0,
    uiOpacityMul: 1,
    uiScaleMul: 1,
    pressed: false,
    pressScaleMul: 1,
    ringIndex: 0,
    slotIndex: slotIndex,
  };

  applyItemAppearance(item);
  applyItemSlot(item, slotIndex, true);
  setItemInstallArcVisible(item, false);

  el.addEventListener('pointerdown', e => {
    e.preventDefault();
    selectItem(item);
  });

  return item;
}

// stepTubeLayout — bridges window.LoopTubeLayoutEngine (classic script).
// Resolved at call time so it's available after the classic script loads.
function stepTubeLayout(layoutState: LayoutState, frameCtx: LayoutFrameCtx): void {
  const win = window as unknown as Record<string, unknown>;
  const engine = win['LoopTubeLayoutEngine'] as { stepTubeLayout: (s: LayoutState, f: LayoutFrameCtx) => void } | undefined;
  if (engine) engine.stepTubeLayout(layoutState, frameCtx);
}

export function getHomeLayoutConfig(): LayoutConfigInput {
  const flowBase = FLUID_TARGET_LERP_BASE * ORGANISM_OUTPUT_LERP_MULT;
  const flowBoost = FLUID_TARGET_LERP_BOOST * ORGANISM_OUTPUT_LERP_MULT;
  return {
    pairGap: ORGANISM_PAIR_GAP,
    centerGuardRadius: LINE_CENTER_RADIUS + 4,
    tubePads: {
      inner: ORGANISM_TUBE_INNER_EDGE_PAD,
      outer: ORGANISM_TUBE_OUTER_EDGE_PAD,
      scene: ORGANISM_BOUNDARY_PADDING,
    },
    scaleLimits: {
      min: ORGANISM_SCALE_MIN,
      max: ORGANISM_SCALE_MAX,
    },
    solverPasses: {
      pair: Math.max(5, ORGANISM_PAIR_PASSES + 1),
      final: Math.max(2, ORGANISM_FINAL_PASSES - 1),
    },
    flowLerp: {
      base: isReducedMotion() ? flowBase * 1.3 : flowBase,
      boost: isReducedMotion() ? flowBoost * 0.75 : flowBoost,
    },
    anchorPull: ORGANISM_ANCHOR_PULL,
    scaleLerp: isReducedMotion() ? 0.24 : FLUID_SCALE_LERP_HIGHLIGHT,
    pressureShrink: ORGANISM_FINAL_SHRINK * 0.5,
    pressureMaxShrink: ORGANISM_FINAL_MAX_SHRINK,
    primaryPinWeight: ORGANISM_PRIMARY_SOLVER_PRIORITY,
  };
}
